import { useDeferredValue, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type {
  OrgUnitDecision,
  OrgValidationBatchResult,
  OrgValidationBatchUpdate,
  OrgValidationNode,
  OrgValidationResult,
  OrgValidationTree,
  OrgValidationUpdate,
} from "@/api/diagnostic-org-structure";
import { OverviewCell } from "@/components/projects/diagnostic/acts/environment/EnvironmentOverviewCard";
import { StackedBar } from "@/components/projects/diagnostic/primitives/StackedBar";
import {
  buildOrgTree,
  collapseToCompanyCodeKeys,
  companyTabs,
  expandAllKeys,
  initialExpandedKeys,
  orgIndicators,
  orgUnitId,
  orgUnitRef,
  selectablePendingIds,
  selectableUnitIds,
  type OrgIndicators,
} from "@/lib/diagnostic-org-tree";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

import { previewDecision } from "@/lib/diagnostic-org-propagation";
import { validationSummaryTsv } from "@/lib/diagnostic-org-summary";

import { useDecisionLabels } from "./decisionLabels";
import { OrgBatchBar } from "./OrgBatchBar";
import { OrgCompanySelect } from "./OrgCompanySelect";
import { OrgSearchField } from "./OrgSearchField";
import { useOrgLevelLabels } from "./orgLevelLabels";
import { ORG_PAGE_SIZE, OrgStructureTable } from "./OrgStructureTable";
import { useBatchErrorMessage, useValidationErrorMessage } from "./validationErrorMessage";
import type { ValidationPatch } from "./ValidationCells";

/** O que a tela mostra depois de gravar ou copiar: o aviso do que aconteceu, ou o erro. */
type Feedback = { kind: "notice"; text: string } | { kind: "error"; text: string };

/**
 * A estrutura organizacional levantada, para validar com o cliente (EF-DIAG-002).
 *
 * Apresentacional: recebe a árvore pronta e, para editar, por onde gravar. O
 * filtro e o que está aberto são estado da tela, e os indicadores seguem o filtro.
 *
 * Antes de gravar uma decisão de escopo, a tela calcula a prévia da propagação,
 * para dizer quantos objetos abaixo mudaram. Quantos voltaram a Pendente vem da
 * resposta do servidor, que é quem decide. Depois, a árvore é relida.
 */
export function OrgStructureView({
  tree,
  canEdit = false,
  onSave,
  onSaveBatch,
}: {
  tree: OrgValidationTree;
  canEdit?: boolean;
  onSave?(body: OrgValidationUpdate): Promise<OrgValidationResult>;
  onSaveBatch?(body: OrgValidationBatchUpdate): Promise<OrgValidationBatchResult>;
}) {
  const { t } = useTranslation();
  const errorMessage = useValidationErrorMessage();
  const batchErrorMessage = useBatchErrorMessage();
  const decisions = useDecisionLabels();
  const levels = useOrgLevelLabels();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  // Quantas linhas a tabela desenha. Só volta ao começo quando a árvore muda de
  // forma (empresas, "Expandir tudo", "Recolher"): abrir uma linha ou reler a árvore
  // depois de salvar não devolve a pessoa ao topo (D38).
  const [limit, setLimit] = useState(ORG_PAGE_SIZE);
  // As empresas marcadas no select; vazia quer dizer todas (D42).
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  // A busca por código ou nome (D43). O campo segue o que se digita; a árvore, o
  // valor adiado, para a digitação não esperar a árvore ser remontada.
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const searching = deferredSearch.trim() !== "";
  // Ordenada: o mesmo conjunto marcado em outra ordem é a mesma vista.
  const filterKey = `${[...selectedCompanies].sort().join("|")}#${deferredSearch}`;
  const rows = useMemo(
    () => buildOrgTree(tree, selectedCompanies, deferredSearch),
    [tree, selectedCompanies, deferredSearch],
  );
  // Com busca, a árvore abre até os achados.
  const initialKeys = useMemo(
    () => (searching ? expandAllKeys(rows) : initialExpandedKeys(rows)),
    [rows, searching],
  );
  // O que está aberto vale para o filtro e a busca em que foi aberto. Trocar de um
  // ou de outra volta ao `initialKeys` da árvore nova, já memorizada, sem montá-la
  // uma segunda vez.
  const [opened, setOpened] = useState<{ filter: string; keys: Set<string> } | null>(null);
  const expanded = opened && opened.filter === filterKey ? opened.keys : initialKeys;
  const indicators = useMemo(() => orgIndicators(tree, selectedCompanies), [tree, selectedCompanies]);
  const tabs = companyTabs(tree);
  const unclassified = tree.companies.some((company) => company.is_client == null);
  // A coluna de seleção é de quem grava o lote (D9).
  const selecting = canEdit && Boolean(onSaveBatch);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set());
  const [savingBatch, setSavingBatch] = useState(false);
  const selectable = useMemo(
    () => new Set(selecting ? selectableUnitIds(rows) : []),
    [rows, selecting],
  );
  // Os pendentes do filtro inteiro, desenhados ou não (D41). Já são selecionáveis:
  // a regra da herdada é a mesma de `selectableUnitIds`.
  const pending = useMemo(
    () => new Set(selecting ? selectablePendingIds(rows) : []),
    [rows, selecting],
  );
  // A seleção efetiva é sempre a interseção com o que dá para selecionar agora:
  // a unidade que passou a herdar o "fora" numa releitura sai sozinha (D12).
  const selected = useMemo(
    () => new Set([...chosen].filter((id) => selectable.has(id))),
    [chosen, selectable],
  );

  if (!tree.source) {
    return (
      <div
        data-testid="org-structure-empty"
        className="rounded-lg border border-dashed border-[#d4dae3] px-4 py-6 text-[13px] text-[#596b85]"
      >
        {t("The organizational structure has not been read for this project yet.")}{" "}
        {t("It appears after the next Functional analysis.")}
      </div>
    );
  }

  function setExpanded(keys: Set<string>) {
    setOpened({ filter: filterKey, keys });
  }

  function reshape(keys: Set<string>) {
    setExpanded(keys);
    setLimit(ORG_PAGE_SIZE);
  }

  function toggle(key: string) {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  }

  // O select de empresas e a busca passam por aqui: a seleção perde o que saiu da vista.
  function changeView(nextCompanies: string[], nextSearch: string) {
    setSelectedCompanies(nextCompanies);
    setSearch(nextSearch);
    setLimit(ORG_PAGE_SIZE);
    // Sem seleção não há o que podar: pula a montagem da árvore, que custa a cada tecla.
    if (chosen.size === 0) return;
    const nextSelectable = new Set(
      selectableUnitIds(buildOrgTree(tree, nextCompanies, nextSearch)),
    );
    // Trocar de vista grava a interseção: voltar não devolve o que saiu (D12, D43).
    setChosen((current) => new Set([...current].filter((id) => nextSelectable.has(id))));
  }

  // O resumo é leitura: quem só vê também copia, para colar na ata.
  function copySummary() {
    const tsv = validationSummaryTsv(rows, {
      header: [t("Object"), t("Legacy key"), t("Public key"), t("Matches?"), t("Scope"), t("Note")],
      yes: t("Yes"),
      no: t("No"),
      decisions,
      typeLabel: levels.label,
    });
    const objects = tsv.split("\n").length - 1;
    const failed = () =>
      setFeedback({ kind: "error", text: t("The summary could not be copied to the clipboard.") });
    setFeedback(null);
    // Fora de contexto seguro (http sem localhost), o navegador não expõe a área
    // de transferência, e chamar `writeText` quebraria o clique sem aviso.
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (!clipboard) {
      failed();
      return;
    }
    clipboard.writeText(tsv).then(
      () =>
        setFeedback({
          kind: "notice",
          text: t("Objects copied: {{total}}", { total: formatCount(objects) }),
        }),
      failed,
    );
  }

  function save(node: OrgValidationNode, patch: ValidationPatch): Promise<void> {
    if (!onSave) return Promise.resolve();
    const preview =
      "decision" in patch ? previewDecision(tree, orgUnitId(node), patch.decision ?? null) : null;
    setFeedback(null);
    return onSave({ item_type: node.item_type, item_key: node.item_key, ...patch }).then(
      (result) => {
        const parts = [];
        if (preview && preview.affected.length > 0) {
          parts.push(t("Objects affected below: {{total}}", { total: formatCount(preview.affected.length) }));
        }
        if (result.reset_to_pending.length > 0) {
          parts.push(t("Back to Pending: {{total}}", { total: formatCount(result.reset_to_pending.length) }));
        }
        setFeedback(parts.length > 0 ? { kind: "notice", text: parts.join(" · ") } : null);
      },
      (error: unknown) => setFeedback({ kind: "error", text: errorMessage(error) }),
    );
  }

  // Parte da seleção efetiva (`selected`), e não de `chosen`: o que já saiu da
  // vista não volta escondido com o próximo clique.
  function toggleUnit(id: string, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    setChosen(next);
  }

  // Marcar soma os pendentes ao que já estava marcado; desmarcar tira só eles (D41).
  function togglePending(checked: boolean) {
    const next = new Set(selected);
    for (const id of pending) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    setChosen(next);
  }

  function applyBatch(decision: OrgUnitDecision | null) {
    if (!onSaveBatch || savingBatch) return;
    setFeedback(null);
    setSavingBatch(true);
    onSaveBatch({ decision, items: [...selected].map(orgUnitRef) })
      .then(
        (result) => {
          setChosen(new Set());
          const parts = [t("Updated: {{total}}", { total: formatCount(result.updated.length) })];
          if (result.covered_by_parent.length > 0) {
            parts.push(
              t("Covered by the level above: {{total}}", {
                total: formatCount(result.covered_by_parent.length),
              }),
            );
          }
          if (result.affected_below.length > 0) {
            parts.push(
              t("Objects affected below: {{total}}", {
                total: formatCount(result.affected_below.length),
              }),
            );
          }
          if (result.reset_to_pending.length > 0) {
            parts.push(
              t("Back to Pending: {{total}}", { total: formatCount(result.reset_to_pending.length) }),
            );
          }
          setFeedback({ kind: "notice", text: parts.join(" · ") });
        },
        // Na falha a seleção fica; a releitura (do hook) tira o que ficou herdado.
        (error: unknown) => setFeedback({ kind: "error", text: batchErrorMessage(error) }),
      )
      .finally(() => setSavingBatch(false));
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
        {t("Check with the client whether each object matches and whether it belongs in the delivery scope.")}
      </p>

      <Indicators indicators={indicators} />

      {unclassified && (
        <p
          data-testid="org-unclassified-note"
          className="rounded-md border border-[#f5d98f] bg-[#fffaeb] px-3 py-2 text-[12px] text-[#8a5a00]"
        >
          {tree.source?.demo_data
            ? t("Demo system: all companies are shown, without separating the client's.")
            : t("The company codes have not been split yet between the client's and the SAP model companies, so all of them are shown.")}
        </p>
      )}

      <div className="rounded-lg border border-[#e3e8ef] bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#eef1f5] p-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <OrgSearchField value={search} onChange={(next) => changeView(selectedCompanies, next)} />
            <OrgCompanySelect
              companies={tabs}
              selected={selectedCompanies}
              onChange={(next) => changeView(next, search)}
            />
          </div>
          <div className="flex gap-2">
            <ToolbarButton onClick={() => reshape(expandAllKeys(rows))}>
              {t("Expand everything")}
            </ToolbarButton>
            <ToolbarButton onClick={() => reshape(collapseToCompanyCodeKeys(rows))}>
              {t("Collapse")}
            </ToolbarButton>
            <ToolbarButton primary onClick={copySummary}>
              {t("Copy validation summary")}
            </ToolbarButton>
          </div>
        </div>
        {selected.size > 0 && (
          <OrgBatchBar count={selected.size} saving={savingBatch} onApply={applyBatch} />
        )}
        {feedback && (
          <p
            role={feedback.kind === "error" ? "alert" : "status"}
            className={cn(
              "border-b border-[#eef1f5] px-3 py-2 text-[12px]",
              feedback.kind === "error" ? "bg-[#fdf3f2] text-[#a23b2f]" : "bg-[#f5f8fc] text-[#08254c]",
            )}
          >
            {feedback.text}
          </p>
        )}
        <OrgStructureTable
          rows={rows}
          expanded={expanded}
          onToggle={toggle}
          limit={limit}
          searching={searching}
          onLoadMore={() => setLimit((current) => current + ORG_PAGE_SIZE)}
          editing={{ canEdit: canEdit && Boolean(onSave), onSave: save }}
          selection={
            selecting
              ? {
                  selected,
                  selectable,
                  pending,
                  disabled: savingBatch,
                  onToggle: toggleUnit,
                  onTogglePending: togglePending,
                }
              : undefined
          }
        />
        <p className="px-3 py-2.5 text-[11px] text-[#7b8799]">
          {t("Rows with a blue background are objects above the company code, or not linked to a company. Marking an object out of scope takes everything below it along.")}
        </p>
      </div>
    </div>
  );
}

function ToolbarButton({
  onClick,
  primary = false,
  children,
}: {
  onClick: () => void;
  /** O botão escuro do protótipo, para a ação principal da barra. */
  primary?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md border px-3 py-1.5 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c] focus-visible:ring-offset-1",
        primary
          ? "border-[#08254c] bg-[#08254c] text-white hover:bg-[#0d3366]"
          : "border-[#d4dae3] text-[#3c4b61] hover:border-[#08254c] hover:text-[#08254c]",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Um indicador da tela, com a mesma célula do card do Ato 1 dentro de uma moldura:
 * o tile "Empresas" e os números daqui não podem divergir de estilo.
 */
function IndicatorCard({
  testId,
  label,
  value,
  hint,
  children,
}: {
  testId: string;
  label: string;
  value: string;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-[#e3e8ef] bg-white p-4">
      <OverviewCell testId={testId} label={label} value={value} hint={hint}>
        {children}
      </OverviewCell>
    </div>
  );
}

function Indicators({ indicators }: { indicators: OrgIndicators }) {
  const { t } = useTranslation();
  const decisions = useDecisionLabels();
  const levels = useOrgLevelLabels();
  const { companyCodes, validation } = indicators;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <IndicatorCard
          testId="org-indicator-company-codes"
          label={t("Company codes in view")}
          value={formatCount(companyCodes.total)}
          hint={t("{{inScope}} in scope · {{out}} out · {{open}} open", {
            inScope: formatCount(companyCodes.inScope),
            out: formatCount(companyCodes.outOfScope),
            open: formatCount(companyCodes.open),
          })}
        />

        <IndicatorCard
          testId="org-indicator-validation"
          label={t("Validation with the client")}
          value={`${validation.decidedPct}%`}
          hint={t("{{decided}} of {{total}} objects decided · {{client}} to validate with the client · {{pending}} pending", {
            decided: formatCount(validation.inScope + validation.outOfScope),
            total: formatCount(validation.total),
            client: formatCount(validation.clientValidation),
            pending: formatCount(validation.pending),
          })}
        >
          <StackedBar
            className="mt-2"
            height="h-2"
            legend
            segments={[
              { key: "in_scope", label: decisions.in_scope, className: "bg-[#27a155]", value: validation.inScope },
              { key: "client_validation", label: decisions.client_validation, className: "bg-[#ffc83d]", value: validation.clientValidation },
              { key: "out_of_scope", label: decisions.out_of_scope, className: "bg-[#596b85]", value: validation.outOfScope },
              { key: "pending", label: decisions.pending, className: "bg-[#d4dae3]", value: validation.pending },
            ]}
          />
        </IndicatorCard>

        <IndicatorCard
          testId="org-indicator-mismatches"
          label={t("Data that does not match")}
          value={formatCount(indicators.mismatches)}
          hint={t("objects marked “No” in Matches?")}
        />
      </div>

      <IndicatorCard
        testId="org-indicator-objects"
        label={t("Objects found")}
        value={formatCount(validation.total)}
      >
        <ul className="mt-3 flex flex-wrap gap-2">
          {indicators.byType.map((entry) => (
            <li
              key={entry.levelCode}
              className="rounded-full border border-[#e3e8ef] bg-[#f5f8fc] px-2.5 py-0.5 text-[11px] text-[#3c4b61]"
            >
              {levels.label(entry.levelCode, entry.levelCode)}{" "}
              <strong className="font-semibold">{formatCount(entry.count)}</strong>
            </li>
          ))}
        </ul>
      </IndicatorCard>
    </div>
  );
}
